// ===========================================
// Mystery Cats Qty Cap (MAX 5)
// Variant: 56025608880294
// Enforces in: SlideCart + /cart page
// ===========================================

(() => {
  const CFG = {
    variantId: "56025608880294",
    maxQty: 5,
    logPrefix: "[MysteryCatsCap]",
    // debounce interval for DOM churn / slidecart rerenders
    debounceMs: 300,
  };

  // ----------------------------
  // Cart helpers
  // ----------------------------
  async function getCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    return res.json();
  }

  async function changeLineItem(key, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: key, quantity }),
    });
  }

  function clampInt(n, min, max) {
    const x = Number(n);
    if (!Number.isFinite(x)) return min;
    return Math.min(Math.max(Math.trunc(x), min), max);
  }

  function bindOnce(el, key, binder) {
    if (!el) return;
    const k = `mcBound_${key}`;
    if (el.dataset?.[k]) return;
    el.dataset[k] = "1";
    binder(el);
  }

  // ----------------------------
  // Variant parsing on /cart page
  // ----------------------------
  function getVariantIdFromCartQtyInput(input) {
    // common datasets
    if (input?.dataset?.quantityVariantId) return String(input.dataset.quantityVariantId);
    if (input?.dataset?.variantId) return String(input.dataset.variantId);

    // Dawn-ish IDs: updates_large_123, updates_small_123, updates_123
    const id = input?.id || "";
    const m = id.match(/updates_(?:large_|small_)?(\d+)/);
    if (m) return String(m[1]);

    return null;
  }

  // ----------------------------
  // Core enforcement: cart clamp
  // ----------------------------
  function getMatchingLines(cart) {
    return (cart?.items || []).filter(
      (it) => String(it.variant_id) === String(CFG.variantId)
    );
  }

  function totalQty(lines) {
    return lines.reduce((sum, it) => sum + (it.quantity || 0), 0);
  }

  async function clampCartToMax(cart) {
    const lines = getMatchingLines(cart);
    if (!lines.length) return { changed: false, total: 0 };

    const total = totalQty(lines);
    if (total <= CFG.maxQty) return { changed: false, total };

    console.log(`${CFG.logPrefix} Total ${total} > ${CFG.maxQty}. Clamping…`);

    let remaining = CFG.maxQty;

    // Clamp across all lines (handles split lines)
    for (const item of lines) {
      const desired = Math.min(item.quantity, remaining);

      if (desired !== item.quantity) {
        const res = await changeLineItem(item.key, desired);
        if (!res.ok) {
          console.warn(`${CFG.logPrefix} /cart/change.js non-OK`, res.status);
        }
      }

      remaining -= desired;
      if (remaining <= 0) remaining = 0;
    }

    window.SLIDECART_UPDATE?.();
    return { changed: true, total: CFG.maxQty };
  }

  // ----------------------------
  // UI enforcement: SlideCart + /cart
  // ----------------------------
  function applyUI(total) {
    const variantId = String(CFG.variantId);
    const maxQty = CFG.maxQty;

    // ---- SlideCart (SlideCart HQ style) ----
    document.querySelectorAll(`[data-variant-id="${variantId}"]`).forEach((line) => {
      const dec = line.querySelector('button[aria-label="decrease quantity"]');
      const inc = line.querySelector('button[aria-label="increase quantity"]');

      const input =
        line.querySelector(".quantity-selector input[type='text']") ||
        line.querySelector(".quantity-selector input[type='number']") ||
        line.querySelector("input[type='number']");

      if (input) {
        input.setAttribute("min", "1");
        input.setAttribute("max", String(maxQty));

        bindOnce(input, "slidecartInput", (el) => {
          const clampAndTrigger = () => {
            const desired = clampInt(el.value, 1, maxQty);
            if (String(desired) !== String(el.value)) el.value = String(desired);
            schedule(0);
          };
          el.addEventListener("change", clampAndTrigger);
          el.addEventListener("blur", clampAndTrigger);
        });
      }

      const lineQty = input ? clampInt(input.value, 1, 999) : 1;
      const disableInc = total >= maxQty || lineQty >= maxQty;

      if (inc) {
        inc.disabled = disableInc;
        inc.setAttribute("aria-disabled", String(disableInc));

        // hard stop even if SlideCart attaches other handlers
        bindOnce(inc, "slidecartInc", (btn) => {
          btn.addEventListener(
            "click",
            (e) => {
              if (btn.disabled) {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }
            },
            true
          );
        });
      }

      // decrement should remain allowed
      if (dec) {
        dec.disabled = false;
        dec.removeAttribute("aria-disabled");
      }
    });

    // ---- /cart page (Dawn-ish) ----
    document.querySelectorAll(".cart__qty-input").forEach((input) => {
      const vId = getVariantIdFromCartQtyInput(input);
      if (String(vId) !== variantId) return;

      input.setAttribute("min", "1");
      input.setAttribute("max", String(maxQty));

      const wrapper =
        input.closest("quantity-input") ||
        input.closest(".quantity") ||
        input.parentElement;

      const plusBtn =
        wrapper?.querySelector('button[name="plus"]') ||
        wrapper?.querySelector('button[aria-label*="increase"]') ||
        wrapper?.querySelector('button[aria-label*="Increase"]');

      const currentQty = clampInt(input.value, 1, 999);
      const disablePlus = total >= maxQty || currentQty >= maxQty;

      if (plusBtn) {
        plusBtn.disabled = disablePlus;

        bindOnce(plusBtn, "cartPlus", (btn) => {
          btn.addEventListener(
            "click",
            (e) => {
              if (btn.disabled) {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }
            },
            true
          );
        });
      }

      bindOnce(input, "cartInput", (el) => {
        const clampAndTrigger = () => {
          const desired = clampInt(el.value, 1, maxQty);
          if (String(desired) !== String(el.value)) el.value = String(desired);
          schedule(0);
        };
        el.addEventListener("change", clampAndTrigger);
        el.addEventListener("blur", clampAndTrigger);
      });
    });
  }

  // ----------------------------
  // Scheduler + observer (one, clean)
  // ----------------------------
  let t = null;
  let inFlight = false;
  let rerun = false;

  function schedule(delay = CFG.debounceMs) {
    clearTimeout(t);
    t = setTimeout(run, delay);
  }

  async function run() {
    if (inFlight) {
      rerun = true;
      return;
    }

    inFlight = true;
    rerun = false;

    try {
      const cart = await getCart();

      // clamp cart first
      const { changed, total } = await clampCartToMax(cart);

      // if changed, re-fetch to get correct totals for UI
      if (changed) {
        const cart2 = await getCart();
        const lines2 = getMatchingLines(cart2);
        applyUI(Math.min(totalQty(lines2), CFG.maxQty));
      } else {
        const lines = getMatchingLines(cart);
        applyUI(Math.min(totalQty(lines), CFG.maxQty));
      }
    } catch (e) {
      console.error(`${CFG.logPrefix} error`, e);
    } finally {
      inFlight = false;
      if (rerun) schedule(150);
    }
  }

  const observer = new MutationObserver(() => schedule(CFG.debounceMs));
  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener("DOMContentLoaded", () => {
    console.log(`${CFG.logPrefix} DOM ready — enforcing max ${CFG.maxQty}…`);
    schedule(0);
  });

  // Optional: expose a manual hook in case you ever need to force refresh
  window.MYSTERY_CATS_ENFORCE_QTY_CAP = () => schedule(0);
})();
